import React, { useState } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import inventoryApi from '../../../services/inventoryApi';
import { downloadXlsxFromAoa } from '../../../utils/excelJsSheetHelpers';
import {
  ReportShell, Field, DateInput, WarehouseSelect, ProductFilters, EMPTY_PRODUCT, productParams, productFilterLine,
  ExportButtons, ViewToggle, printReport, runReport, qty, money, today, fmtDate, esc,
} from './shared/ReportKit';

const DEFAULT_BANDS = '50,100,150,200,250,300,360';
const TYPE_LABEL = { receipt: 'Receipt', adjustment_in: 'Adjustment +', return_in: 'Return in', transfer_in: 'Transfer in', no_receipt_record: 'No receipt record' };

// Stock Age Analysis: stock on hand matched to the latest receipts (first in, first out), each aged
// from its receipt date, in day bands chosen by the user
const StockAgeAnalysisReport = () => {
  const [asOfDate, setAsOfDate] = useState(today());
  const [warehouse, setWarehouse] = useState({ id: '', label: '' });
  const [product, setProduct] = useState(EMPTY_PRODUCT);
  const [bands, setBands] = useState(DEFAULT_BANDS);
  const [view, setView] = useState('summary');
  const [measure, setMeasure] = useState('quantity');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const generate = () => runReport(() => inventoryApi.getStockAgeAnalysis({
    asOfDate, buckets: bands.replace(/\s+/g, ''), ...(warehouse.id ? { warehouseId: warehouse.id } : {}), ...productParams(product),
  }), setLoading, setData);
  const reset = () => { setAsOfDate(today()); setWarehouse({ id: '', label: '' }); setProduct(EMPTY_PRODUCT); setBands(DEFAULT_BANDS); setData(null); };
  const isVal = measure === 'value';
  const cell = (r, b) => (isVal ? r.valueByBand[b.key] : r.qtyByBand[b.key]);
  const fmt = (v) => (isVal ? money(v) : qty(v));
  const filtersLine = [warehouse.label && `Location: ${warehouse.label}`, productFilterLine(product)].filter(Boolean).join(' · ');
  const subtitle = data ? `As at ${fmtDate(data.asOfDate)} · ${isVal ? 'Value' : 'Quantity'} · ${view === 'detail' ? 'Detail' : 'Summary'}` : '';

  const exportExcel = async () => {
    const rows = [['STOCK AGE ANALYSIS'], [subtitle], ...(filtersLine ? [[filtersLine]] : []), [],
      ['Item Code', 'Item Name', ...data.bands.map(b => b.label), 'Total']];
    data.rows.forEach(r => {
      rows.push([r.itemCode, r.itemName, ...data.bands.map(b => cell(r, b)), isVal ? r.value : r.quantity]);
      if (view === 'detail') r.layers.forEach(l => {
        const idx = data.bands.findIndex(b => b.key === l.band);
        rows.push([`   ${l.date ? fmtDate(l.date) : '—'}`, `   ${l.documentNo || TYPE_LABEL[l.type] || l.type} (${l.days ?? '—'} days)`, ...data.bands.map((b, i) => (i === idx ? (isVal ? l.value : l.qty) : '')), '']);
      });
    });
    rows.push([], ['', 'TOTAL', ...data.bands.map(b => (isVal ? data.totals.valueByBand[b.key] : data.totals.qtyByBand[b.key])), isVal ? data.totals.value : data.totals.quantity]);
    await downloadXlsxFromAoa(rows, 'Stock Age', `stock-age-${data.asOfDate}.xlsx`, [{ wch: 18 }, { wch: 38 }, ...data.bands.map(() => ({ wch: 12 })), { wch: 14 }]);
  };
  const print = () => printReport({
    title: 'Stock Age Analysis', subtitle, filtersLine, landscape: true,
    tableHtml: `<table><thead><tr><th>Item Code</th><th>Item Name</th>${data.bands.map(b => `<th class="r">${esc(b.label)}</th>`).join('')}<th class="r">Total</th></tr></thead><tbody>${
      data.rows.map(r => `<tr${view === 'detail' ? ' class="group"' : ''}><td>${esc(r.itemCode)}</td><td>${esc(r.itemName)}</td>${data.bands.map(b => `<td class="r">${cell(r, b) ? fmt(cell(r, b)) : ''}</td>`).join('')}<td class="r"><b>${fmt(isVal ? r.value : r.quantity)}</b></td></tr>${
        view === 'detail' ? r.layers.map(l => `<tr class="child"><td>${l.date ? fmtDate(l.date) : '—'}</td><td>${esc(l.documentNo || TYPE_LABEL[l.type] || l.type)} · ${l.days ?? '—'} days</td>${data.bands.map(b => `<td class="r">${b.key === l.band ? fmt(isVal ? l.value : l.qty) : ''}</td>`).join('')}<td></td></tr>`).join('') : ''
      }`).join('')
    }<tr class="total"><td></td><td>Total</td>${data.bands.map(b => `<td class="r">${fmt(isVal ? data.totals.valueByBand[b.key] : data.totals.qtyByBand[b.key])}</td>`).join('')}<td class="r">${fmt(isVal ? data.totals.value : data.totals.quantity)}</td></tr></tbody></table>`,
    note: 'Stock on hand is matched to the latest receipts (first in, first out) and aged from each receipt date.',
  });

  return (
    <ReportShell
      title="Stock Age Analysis" subtitle="Stock on hand by age since receipt, in day bands" icon={ClockIcon}
      loading={loading} onGenerate={generate} onReset={reset}
      filters={<>
        <Field label="As at Date"><DateInput value={asOfDate} onChange={setAsOfDate} /></Field>
        <Field label="Location"><WarehouseSelect value={warehouse.id} onChange={(id, label) => setWarehouse({ id, label: id ? label : '' })} /></Field>
        <ProductFilters value={product} onChange={setProduct} />
        <Field label="Age bands (days)">
          <input value={bands} onChange={(e) => setBands(e.target.value)} placeholder={DEFAULT_BANDS} title="Upper limit of each band, comma separated — anything older goes to 'Over'"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 w-[230px] font-mono" />
        </Field>
      </>}
      actions={data && <>
        <div className="flex items-center gap-3">
          <ViewToggle value={view} onChange={setView} options={[{ value: 'summary', label: 'Summary' }, { value: 'detail', label: 'Detail' }]} />
          <ViewToggle value={measure} onChange={setMeasure} options={[{ value: 'quantity', label: 'Quantity' }, { value: 'value', label: 'Value' }]} />
          <span className="text-sm text-indigo-600 font-medium">As at {fmtDate(data.asOfDate)}</span>
        </div>
        <ExportButtons onExcel={exportExcel} onPrint={print} />
      </>}
    >
      {data && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Item Code</th>
                <th className="text-left px-4 py-2 font-medium">Item Name</th>
                {data.bands.map(b => <th key={b.key} className="text-right px-3 py-2 font-medium whitespace-nowrap">{b.label}</th>)}
                <th className="text-right px-4 py-2 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.length === 0 && <tr><td colSpan={data.bands.length + 3} className="px-4 py-8 text-center text-gray-400 italic">No stock on hand for these filters</td></tr>}
              {data.rows.map(r => (
                <React.Fragment key={r.itemId}>
                  <tr className={view === 'detail' ? 'bg-indigo-50/60 font-medium' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{r.itemCode}</td>
                    <td className="px-4 py-2 min-w-[180px]">{r.itemName}</td>
                    {data.bands.map((b, i) => <td key={b.key} className={`px-3 py-2 text-right font-mono ${cell(r, b) ? (i >= data.bands.length - 2 ? 'text-red-600' : '') : 'text-gray-300'}`}>{cell(r, b) ? fmt(cell(r, b)) : '—'}</td>)}
                    <td className="px-4 py-2 text-right font-mono font-semibold">{fmt(isVal ? r.value : r.quantity)}</td>
                  </tr>
                  {view === 'detail' && r.layers.map((l, i) => (
                    <tr key={i} className="text-xs text-gray-600">
                      <td className="pl-8 pr-4 py-1">{l.date ? fmtDate(l.date) : '—'}</td>
                      <td className="px-4 py-1">{l.documentNo || ''} <span className="text-gray-400">{TYPE_LABEL[l.type] || l.type} · {l.days ?? '—'} days</span></td>
                      {data.bands.map(b => <td key={b.key} className="px-3 py-1 text-right font-mono">{b.key === l.band ? fmt(isVal ? l.value : l.qty) : ''}</td>)}
                      <td />
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {data.rows.length > 0 && (
                <tr className="bg-gray-100 font-semibold">
                  <td colSpan={2} className="px-4 py-2">Total</td>
                  {data.bands.map(b => <td key={b.key} className="px-3 py-2 text-right font-mono">{fmt(isVal ? data.totals.valueByBand[b.key] : data.totals.qtyByBand[b.key])}</td>)}
                  <td className="px-4 py-2 text-right font-mono">{fmt(isVal ? data.totals.value : data.totals.quantity)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </ReportShell>
  );
};

export default StockAgeAnalysisReport;
